import { useEffect, type ReactElement } from "react";
import { useLocation } from "react-router";

/** 画面を移るときに添える値（API の notice / detail。docs/05 §11.7）。 */
export interface MoveState {
    readonly notice?: string | undefined;
    readonly detail?: string | undefined;
}

const stateOf = (state: unknown): MoveState =>
    typeof state === "object" && state !== null ? state : {};

/** 移った先の画面が出す値（確認メールを送ったアドレスなど）。 */
export const useDetail = (): string | undefined => stateOf(useLocation().state).detail;

/**
 * 出した通知を、ブラウザの履歴からは消す。<b>再読み込みでもう一度出さない</b>
 * （通知は 1 度だけ出す）。
 *
 * React Router は移るときの値を <c>history.state.usr</c> に置き、再読み込みするとそこから読み直す。
 * <b>Router には知らせずに履歴だけを書き換える</b>——いま出ている通知は、次に移るまで出たままになる。
 * 添えたアドレス（detail）は画面の中身なので残す。
 */
const forgetNotice = (): void => {
    const state: unknown = window.history.state;
    if (typeof state !== "object" || state === null || !("usr" in state)) {
        return;
    }
    const { usr } = state as { readonly usr: unknown };
    if (typeof usr === "object" && usr !== null && "notice" in usr) {
        window.history.replaceState({ ...state, usr: { ...usr, notice: undefined } }, "");
    }
};

/**
 * 画面を移った後の通知（「パスワードを変更しました。」）。
 * どの画面でも中身の上に、同じ見た目で出す。
 */
export function Notice(): ReactElement | null {
    const location = useLocation();
    const { notice } = stateOf(location.state);

    useEffect(() => {
        if (notice !== undefined) {
            forgetNotice();
        }
    }, [notice, location.key]);

    return notice === undefined ? null : <div className="alert-success mb-5" role="status">{notice}</div>;
}
